import Big from 'big.js';
import * as bootstrap from 'bootstrap';
import { UpdateCartItems } from 'smg/customer-api/v2/carts';
import * as filepath from 'smg/filepath';
// import * as html from 'smg/html';
import * as models from 'smg/customer-api/models';
import { ShowError } from 'smg/errors';
import Modal from 'smg/modal';
import QueryString from 'smg/querystring';
import * as strings from 'smg/strings';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import { Load } from 'smg/uploads';
import { DollarFormat } from 'smg/util';
import { SourceOrder } from 'smg/cart-items';
import * as carts from '../carts';
import { Relref } from '../hugo';
import * as lineitems from '../lineitems';
import { PromptQuantityRequest, SetupRevisionLink } from '../quotes';

declare interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

void main();

async function main() {
    const quoteID = QueryString().get('quote');
    const orderID = QueryString().get('order');
    const addCartID = QueryString().get('cart');
    if (quoteID) {
        await carts.AddCartItem({ quoteID }, {
            promptToCheckout: false,
            newCart: true,
        });
    }
    if (orderID) {
        await carts.AddCartItem({ orderID }, {
            promptToCheckout: false,
            newCart: true,
        });
    }
    if (addCartID) {
        await carts.AddCartItem({ cartID: addCartID }, {
            promptToCheckout: false,
        });
    }

    const cartCard = document.querySelector('#cart-card') as HTMLElement;
    const subtotal = Array.from(document.querySelectorAll('.cart-subtotal'));
    const $subtotalSpinner = document.querySelector('#subtotal-spinner') as HTMLElement;
    const prepaymentParents = Array.from(document.querySelectorAll('.cart-prepayment-parent'));
    const prepaymentEls = Array.from(document.querySelectorAll('.cart-prepayment'));
    const availableDiscountParents = Array.from(document.querySelectorAll('.cart-available-discount-parent'));
    const availableDiscountEls = Array.from(document.querySelectorAll('.cart-available-discount'));

    // total comes back net of any project deposit. Show the gross amount as
    // the subtotal so it visibly adds up against the item cards, and break
    // the deposit out as its own credit line — checkout shows the same line
    // in its full breakdown.
    const drawSummaryTotals = (cart: models.HyejooCart | undefined) => {
        const prepayment = cart?.totals.prepayment ?? Big(0);
        const availableDiscount = cart?.totals.availableDiscount ?? Big(0);
        // total is net of the available-discount spend too, so add it back for the pre-credit subtotal.
        subtotal.forEach(el => el.textContent = DollarFormat((cart?.totals.total ?? Big(0)).add(prepayment).sub(availableDiscount)));
        if (prepayment.gt(0)) {
            prepaymentEls.forEach(el => el.textContent = DollarFormat(Big(0).sub(prepayment)));
            prepaymentParents.forEach(el => el.classList.remove('d-none'));
        } else {
            prepaymentParents.forEach(el => el.classList.add('d-none'));
        }
        if (availableDiscount.eq(0)) {
            availableDiscountParents.forEach(el => el.classList.add('d-none'));
        } else {
            availableDiscountEls.forEach(el => el.textContent = DollarFormat(availableDiscount));
            availableDiscountParents.forEach(el => el.classList.remove('d-none'));
        }
    };

    const cartItemTemplate = template.Clone('#cart-item-template').firstElementChild as HTMLElement;
    const $cartItemUploadTemplate = template.Clone('#cart-item-upload-template').firstElementChild as HTMLElement;

    const cartItemLineTemplate = cartItemTemplate.querySelector('.cart-item-line') as HTMLElement;
    cartItemLineTemplate.parentElement?.removeChild(cartItemLineTemplate);

    const cartItemProofTemplate = cartItemTemplate.querySelector('.cart-item-footer-proof') as HTMLElement;

    const spinner = document.querySelector('#cart-spinner') as HTMLElement;
    const checkoutButtons: HTMLAnchorElement[] = Array.from(document.querySelectorAll('.checkout-button'));

    const cartEmpty = document.querySelector('#cart-empty') as HTMLElement;
    cartEmpty.parentElement?.removeChild(cartEmpty);
    cartEmpty.classList.remove('d-none');

    const showEmptyCart = () => {
        hideLoading();

        cartCard.classList.remove('d-none');
        cartCard.innerHTML = '';
        cartCard.appendChild(cartEmpty);
    };

    const drawCartItem = (cartItem: models.CartItem): HTMLElement => {
        const soldOrder = SourceOrder(cartItem);
        if (cartItem.upload) {
            const $cartItemEl = $cartItemUploadTemplate.cloneNode(true) as HTMLElement;

            const $titleEls = Array.from($cartItemEl.querySelectorAll('.cart-item-title'));
            if (cartItem.uploadLineItem.name) {
                for (const $titleEl of $titleEls) {
                    $titleEl.textContent = cartItem.uploadLineItem.name;
                }
            }
            if (cartItem.order) {
                for (const $titleEl of $titleEls) {
                    const $reorderOf = document.createElement('span');
                    $reorderOf.className = 'd-block small fw-normal text-muted';
                    $reorderOf.textContent = `Reorder of ${cartItem.order.invoiceNumber}`;
                    $titleEl.append($reorderOf);
                }
            }

            const $cartItemImage = $cartItemEl.querySelector<HTMLElement>('.cart-item-image-container');
            const name = cartItem.uploadLineItem.name || cartItem.upload.name || 'Custom Design';
            if ($cartItemImage) {
                $cartItemImage.classList.remove('d-none');
                const $a = $cartItemImage.querySelector('a');
                if ($a) {
                    $a.href = `${window.cloudFront}/${cartItem.upload.key ?? ''}`;
                    $a.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);
                    $a.setAttribute('data-caption', 'Custom Design');
                }
                const $img = $cartItemImage.querySelector('img');
                if ($img) {
                    void Load($img, `${window.cloudFront}/${cartItem.upload.key ?? ''}`, name, cartItem.upload.size ?? 0);
                }
            }

            carts.DrawBuilderItemDetails($cartItemEl, cartItem, name, window.cloudFront);

            const $qtyInput = $cartItemEl.querySelector('#quantity-input') as HTMLInputElement;
            $qtyInput.value = String(cartItem.quantity);

            const $increaseQty = $cartItemEl.querySelector('#increase-qty') as HTMLButtonElement;
            const $decreaseQty = $cartItemEl.querySelector('#decrease-qty') as HTMLButtonElement;
            const $cartItemTotals = Array.from($cartItemEl.querySelectorAll('.cart-item-total'));
            const $cartItemSpinners = Array.from($cartItemEl.querySelectorAll('.total-spinner'));
            const $cartItemBtnsDivs = Array.from($cartItemEl.querySelectorAll('.cart-control-btns'));
            const $cartItemBtns: Element[] = [];
            for (const $el of $cartItemBtnsDivs) {
                for (const $btn of Array.from($el.children)) {
                    $cartItemBtns.push($btn);
                }
            }

            [$increaseQty, $decreaseQty, $qtyInput].forEach(el => {
                if (el instanceof HTMLInputElement) {
                    el.addEventListener('keypress', e => {
                        if (e.key === 'Enter') {
                            updateCartItemTotal();
                        }
                    });
                    el.addEventListener('blur', e => updateCartItemTotal(e));
                } else {
                    el.addEventListener('click', e => updateCartItemTotal(e));
                }

                if (cartItem.quantity) {
                    for (const $cartItemTotal of $cartItemTotals) {
                        $cartItemTotal.textContent = DollarFormat(cartItem.subtotal || 0);
                    }
                }

                function updateCartItemTotal(e?: Event) {
                    e?.preventDefault();
                    for (const $btn of $cartItemBtns) {
                        $btn.setAttribute('disabled', 'true');
                    }

                    [...$cartItemSpinners, $subtotalSpinner].forEach(el => el.classList.remove('d-none'));
                    subtotal.forEach(el => el.classList.add('d-none'));
                    for (const $cartItemTotal of $cartItemTotals) {
                        $cartItemTotal.classList.add('d-none');
                    }

                    const oldQty = cartItem.quantity || 1;

                    let valNum = parseInt($qtyInput.value, 10);

                    switch (e?.currentTarget) {
                    case $increaseQty:
                        valNum++;
                        break;
                    case $decreaseQty:
                        valNum--;
                        break;
                    case $qtyInput:
                        valNum = parseInt($qtyInput.value, 10);
                        break;
                    default:
                        break;
                    }

                    $qtyInput.value = valNum.toString();

                    void UpdateCartItems([{ id: cartItem.id || '', quantity: valNum }]).then(async changed => {
                        carts.SetCartCount(changed.cartItemsCount);
                        await carts.GetCart('cartItems,totals').then(resp => {
                            drawSummaryTotals(resp);
                            if (resp?.cartItems.length) {
                                for (const updatedCartItem of resp.cartItems) {
                                    if (cartItem.id === updatedCartItem.id) {
                                        for (const $cartItemTotal of $cartItemTotals) {
                                            $cartItemTotal.textContent = DollarFormat(updatedCartItem.subtotal || '0');
                                        }
                                    }
                                }
                            }
                        });
                        [...$cartItemSpinners, $subtotalSpinner].forEach(el => el.classList.add('d-none'));
                        subtotal.forEach(el => el.classList.remove('d-none'));
                        for (const $cartItemTotal of $cartItemTotals) {
                            $cartItemTotal.classList.remove('d-none');
                        }

                        for (const $btn of $cartItemBtns) {
                            $btn.removeAttribute('disabled');
                        }
                    }).catch(err => {
                        ShowError(err);

                        cartItem.quantity = oldQty;
                        for (const $btn of $cartItemBtns) {
                            $btn.removeAttribute('disabled');
                        }
                    });
                }
            });

            // const $editBtn = $cartItemEl.querySelector('#edit-btn') as HTMLButtonElement;
            const $removeBtn = $cartItemEl.querySelector('#remove-btn') as HTMLButtonElement;
            $removeBtn.addEventListener('click', e => {
                e.preventDefault();

                showSubtotalSpinner();
                showLoading();

                let hr: HTMLElement | undefined;
                if ($cartItemEl.nextElementSibling?.nodeName.toLowerCase() === 'hr') {
                    hr = $cartItemEl.nextElementSibling as HTMLElement;
                } else if ($cartItemEl.previousElementSibling?.nodeName.toLowerCase() === 'hr') {
                    hr = $cartItemEl.previousElementSibling as HTMLElement;
                }

                const els = [$cartItemEl];
                if (hr) {
                    els.push(hr);
                }

                els.forEach(el => el.classList.add('d-none'));
                carts.DeleteCartItem(cartItem.id || '', () => void drawCart()).
                    then(() => {
                        if (!carts.GetCurrentCartCount()) {
                            showEmptyCart();
                        }
                        void drawCart();
                    }).
                    catch(err => {
                        els.forEach(el => el.classList.remove('d-none'));
                        throw err;
                    });
            });
            return $cartItemEl;
        }

        const cartItemEl = cartItemTemplate.cloneNode(true) as HTMLElement;

        const title = cartItemEl.querySelector('.cart-item-title') as HTMLElement;

        const itemSubtotal = cartItemEl.querySelector('.cart-item-subtotal') as HTMLElement;
        itemSubtotal.textContent = DollarFormat(cartItem.total || 0);

        const oldSubtotal = cartItemEl.querySelector('.text-decoration-line-through') as HTMLElement;
        if (cartItem.maxNetworkSubtotal && Big(cartItem.maxNetworkSubtotal).gt(cartItem.subtotal || 0)) {
            itemSubtotal.classList.add('text-dark');
            oldSubtotal.textContent = DollarFormat(cartItem.maxNetworkSubtotal);
        } else {
            oldSubtotal.parentElement?.removeChild(oldSubtotal);
        }

        const deleteLink = cartItemEl.querySelector('.cart-item-delete') as HTMLAnchorElement;
        deleteLink.addEventListener('click', e => {
            e.preventDefault();

            showLoading();

            let hr: HTMLElement | undefined;
            if (cartItemEl.nextElementSibling?.nodeName.toLowerCase() === 'hr') {
                hr = cartItemEl.nextElementSibling as HTMLElement;
            } else if (cartItemEl.previousElementSibling?.nodeName.toLowerCase() === 'hr') {
                hr = cartItemEl.previousElementSibling as HTMLElement;
            }

            const els = [cartItemEl];
            if (hr) {
                els.push(hr);
            }

            els.forEach(el => el.classList.add('d-none'));
            void carts.DeleteCartItem(cartItem.id || '', () => void drawCart()).
                then(() => {
                    void drawCart();
                    if (!carts.GetCurrentCartCount()) {
                        showEmptyCart();
                    }
                }).
                catch(err => {
                    els.forEach(el => el.classList.remove('d-none'));
                    throw err;
                });
        });

        const revisionParent = cartItemEl.querySelector('.cart-item-revision-parent') as HTMLElement;
        const revision = revisionParent.querySelector('.cart-item-revision') as HTMLAnchorElement;
        if (cartItem.quote?.id) {
            const revBase = `${Relref('/revisions')}?id=${cartItem.quote.key || ''}`;
            SetupRevisionLink(revision, cartItem.quote.quoteProofs, window.cloudFront, revBase);
        } else {
            revisionParent.parentElement?.removeChild(revisionParent);
        }

        const info = cartItemEl.querySelector('.cart-item-info') as HTMLAnchorElement;
        if (cartItem.quote?.quoteLineItems?.length || soldOrder?.orderLineItems?.length) {
            info.addEventListener('click', async e => {
                e.preventDefault();

                new Modal({
                    title: `Pricing breakdown for ${title.textContent || ''}`,
                    body: await lineitems.Table(cartItem.quote?.quoteLineItems?.length ?
                        models.QuoteLineItem.fromArray(cartItem.quote.quoteLineItems) :
                        models.OrderLineItem.fromArray(soldOrder?.orderLineItems || [])),
                    size: 'modal-lg',
                    cancelText: 'Close',
                });
            });
        } else {
            info.parentElement?.removeChild(info);
        }

        const drawCartItemLine = (lineItem: models.QuoteLineItem | models.OrderLineItem): HTMLElement => {
            const lineItemEl = cartItemLineTemplate.cloneNode(true) as HTMLElement;
            const summary = lineItemEl.querySelector('.cart-item-summary') as HTMLElement;

            const proofLink = lineItemEl.querySelector('.proof-link') as HTMLAnchorElement;
            const proofImg = proofLink.querySelector('img') as HTMLImageElement;
            const proof = lineItem instanceof models.QuoteLineItem ? lineItem.quoteProof?.proof : lineItem.orderProof?.proof;
            let proofName: string = '';
            if (proof?.main) {
                proofLink.href = proof.main;
                proofLink.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);

                proofImg.src = proof.thumbnail || proof.main;

                if (lineItem instanceof models.QuoteLineItem && lineItem.quoteProof?.number) {
                    proofName = `Proof ${lineItem.quoteProof.number || ''}`;
                } else if (lineItem instanceof models.OrderLineItem) {
                    proofName = lineItem.orderProof?.name || '';
                }
                proofLink.setAttribute('data-caption', proofName);

                proofImg.title = proofName;
                proofImg.alt = proofName;
            } else {
                proofLink.parentElement?.removeChild(proofLink);
            }

            summary.textContent = '';
            if (proofName) {
                summary.textContent += proofName;
                summary.textContent += ' - ';
            }

            const qty = lineItem.quantity || 0;
            const product = (qty === 1 ? lineItem.lineItemModel?.name : lineItem.lineItemModel?.pluralName) || lineItem.lineItem;
            summary.textContent += `${Number(qty).toLocaleString()} ${product || ''}`;

            const controls = lineItemEl.querySelector('.cart-item-line-controls') as HTMLElement;
            const changeQty = controls.querySelector('.cart-item-edit-quantity') as HTMLAnchorElement;
            const removeParent = controls.querySelector('.cart-item-line-delete-parent') as HTMLElement;
            const remove = removeParent.querySelector('.cart-item-line-delete') as HTMLAnchorElement;
            if (!lineItem.lineItemModel?.product) {
                controls.parentElement?.removeChild(controls);
            } else {
                if (productCount < 2) {
                    removeParent.parentElement?.removeChild(removeParent);
                }

                removeParent.parentElement?.removeChild(removeParent);

                new bootstrap.Tooltip(changeQty, {
                    title: 'Please contact your sales representative to change your quantity.',
                });
                changeQty.addEventListener('click', e => {
                    e.preventDefault();
                    void PromptQuantityRequest({
                        quote: cartItem.quote ? cartItem.quote : undefined,
                        order: soldOrder,
                        lineItem,
                    });
                });
                remove.addEventListener('click', e => {
                    e.preventDefault();

                    const hide = () => lineItemEl.classList.add('d-none');
                    const show = () => lineItemEl.classList.remove('d-none');

                    const deleteItem = async () => {
                        hide();
                        return UpdateCartItems([{ id: cartItem.id || '', active: false }]).
                            then(() => {
                                void drawCart();

                                toast.Undo('Removed from cart.', () => restore());
                            }).
                            catch(err => {
                                toast.Error('Something went wrong!');
                                show();
                                throw err;
                            });
                    };

                    const restore = async () => {
                        show();
                        return UpdateCartItems([{ id: cartItem.id || '', active: true }]).
                            then(() => {
                                void drawCart();
                                toast.Success('Restored item.');
                            }).
                            catch(err => {
                                toast.Error('Something went wrong!');
                                hide();
                                throw err;
                            });
                    };

                    void deleteItem();
                });
            }

            if (lineItem.invalidCustomQuantity) {
                lineItemEl.querySelector<HTMLElement>('.cart-item-invalid-quantity')?.classList.remove('d-none');
                const editBelowMin = lineItemEl.querySelector<HTMLElement>('.edit-below-min');
                editBelowMin?.addEventListener('click', e => {
                    e.preventDefault();
                    void PromptQuantityRequest({
                        quote: cartItem.quote ? cartItem.quote : undefined,
                        order: soldOrder,
                        lineItem,
                    });
                });
            }

            return lineItemEl;
        };

        const cartItemDescription = cartItemEl.querySelector('.cart-item-description') as HTMLElement;

        const proofs: models.Proof[] = [];
        const shownProofs = new Set<string>();

        let productCount = 0;
        const objectLineItems = cartItem.quote?.quoteLineItems || soldOrder?.orderLineItems;
        if (objectLineItems) {
            for (const objectLineItem of objectLineItems) {
                if (objectLineItem.lineItemModel?.tax || objectLineItem.lineItemModel?.shipping) {
                    continue;
                }

                productCount++;

                if (productCount >= 2) {
                    break;
                }
            }
        }

        let invalidCustomQuantity = false;

        if (cartItem.quote) {
            title.textContent = `Quote ${cartItem.quote.serial || ''}`;
            if (cartItem.order) {
                const $reorderOf = document.createElement('span');
                $reorderOf.className = 'small text-muted ms-2';
                $reorderOf.textContent = `Reorder of ${cartItem.order.invoiceNumber}`;
                title.after($reorderOf);
            }

            if (cartItem.quote.quoteProofs) {
                for (const quoteProof of cartItem.quote.quoteProofs) {
                    if (!quoteProof.proof) {
                        continue;
                    }
                    quoteProof.proof.name = `Proof ${quoteProof.name || ''}`;
                    proofs.push(quoteProof.proof);
                }
            }

            if (cartItem.quote.quoteLineItems) {
                for (const quoteLineItem of cartItem.quote.quoteLineItems) {
                    if (quoteLineItem.lineItemModel?.tax || quoteLineItem.lineItemModel?.shipping) {
                        continue;
                    }

                    cartItemDescription.appendChild(drawCartItemLine(quoteLineItem));

                    if (quoteLineItem.quoteProof?.proof?.id) {
                        shownProofs.add(quoteLineItem.quoteProof?.proof.id);
                    }

                    if (quoteLineItem.invalidCustomQuantity) {
                        invalidCustomQuantity = true;
                    }
                }
            }
        } else if (soldOrder) {
            title.textContent = `Reorder ${soldOrder.invoiceNumber || ''}`;

            if (soldOrder.orderProofs) {
                for (const orderProof of soldOrder.orderProofs) {
                    if (!orderProof.proof) {
                        continue;
                    }
                    orderProof.proof.name = `Proof ${strings.TrimSuffix(orderProof.name || '', filepath.Ext(orderProof.name || ''))}`;
                    proofs.push(orderProof.proof);
                }
            }

            if (soldOrder.orderLineItems) {
                for (const orderLineItem of soldOrder.orderLineItems) {
                    if (orderLineItem.lineItemModel?.tax || orderLineItem.lineItemModel?.shipping) {
                        continue;
                    }

                    cartItemDescription.appendChild(drawCartItemLine(orderLineItem));

                    if (orderLineItem.orderProof?.proof?.id) {
                        shownProofs.add(orderLineItem.orderProof?.proof.id);
                    }

                    if (orderLineItem.invalidCustomQuantity) {
                        invalidCustomQuantity = true;
                    }
                }
            }
        }

        if (invalidCustomQuantity) {
            for (const btn of checkoutButtons) {
                btn.setAttribute('disabled', 'disabled');
            }
        } else {
            for (const btn of checkoutButtons) {
                btn.removeAttribute('disabled');
            }
        }

        const footerProofs = cartItemEl.querySelector('.cart-item-footer-proofs') as HTMLElement;
        let footerHasProofs = false;

        for (const proof of proofs) {
            if (proof.id && shownProofs.has(proof.id)) {
                continue;
            }

            const proofLink = cartItemProofTemplate.cloneNode(true) as HTMLAnchorElement;
            const proofImg = proofLink.querySelector('img') as HTMLImageElement;
            if (proof?.main) {
                proofLink.href = proof.main;
                proofLink.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);
                proofLink.setAttribute('data-caption', proof.name || '');

                proofImg.src = proof.thumbnail || proof.main;

                proofImg.title = proof.name || '';
                proofImg.alt = proof.name || '';
            } else {
                proofLink.parentElement?.removeChild(proofLink);
            }

            footerProofs.appendChild(proofLink);
            footerHasProofs = true;
        }

        if (!footerHasProofs) {
            footerProofs.parentElement?.removeChild(footerProofs);
        }

        return cartItemEl;
    };

    const drawCart = async () => {
        showLoading();
        $subtotalSpinner.classList.remove('d-none');
        subtotal.forEach(el => el.classList.add('d-none'));
        const cartID = carts.GetCartID();

        if (!cartID) {
            showEmptyCart();
            return;
        }

        for (const btn of checkoutButtons) {
            btn.removeAttribute('disabled');
        }

        const cart = await carts.GetCart('cartItems,totals');
        drawSummaryTotals(cart);

        if (!cart?.cartItems.length) {
            showEmptyCart();
            return;
        } else {
            const link = document.querySelector<HTMLElement>('.link');
            link?.classList.remove('d-none');
        }

        const frag = document.createDocumentFragment();
        for (const cartItem of cart.cartItems) {
            frag.appendChild(drawCartItem(cartItem));
        }

        cartCard.innerHTML = '';
        cartCard.appendChild(frag);

        $subtotalSpinner.classList.add('d-none');
        subtotal.forEach(el => el.classList.remove('d-none'));
        hideLoading();
    };

    function showSubtotalSpinner() {
        subtotal.forEach(el => el.classList.add('d-none'));
        $subtotalSpinner.classList.remove('d-none');
    }

    function showLoading() {
        spinner.classList.remove('d-none');
        cartCard.classList.add('d-none');
    }

    function hideLoading() {
        spinner.classList.add('d-none');
        cartCard.classList.remove('d-none');

        $subtotalSpinner.classList.add('d-none');
        subtotal.forEach(el => el.classList.remove('d-none'));
    }

    void drawCart();
}